import React, { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import { AttachmentUploader, AttachmentGallery } from '@components/attachments';
import ImagenGenerator from './ImagenGenerator';
import { EntityType, AttachmentContext } from '@constants/attachments';
import './ImageManager.scss';

const ImageManager = ({
  entity,
  entityType,
  entityId,
  entityName = '',
  editable = false,
  showUploader = true,
  showAiGenerator = true,
  maxImages = 10,
  className = "",
  maxFileSize = 10 * 1024 * 1024, // 10MB par défaut
  context = AttachmentContext.GENERAL,
  displayMode,
  thumbnailSize = null // 80, 120, 300, or null for full size
}) => {
  const { t } = useLanguage();
  const notify = useNotification();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAiTab, setShowAiTab] = useState(false);

  // Extract entity information from entity object or use individual props
  const finalEntityType = entity?.model_type || entityType;
  const finalEntityId = entity?.id || entityId;
  const finalEntityName = entity?.name || entityName;

  const handleUploadSuccess = (attachment) => {
    // Force refresh the gallery
    setRefreshKey(prev => prev + 1);
  };

  const handleAttachmentDelete = (attachmentId) => {
    // Force refresh the gallery
    setRefreshKey(prev => prev + 1);
  };

  const handleAttachmentUpdate = (attachmentId) => {
    // Force refresh the gallery
    setRefreshKey(prev => prev + 1);
  };

  const handleAiImageGenerated = (data) => {
    if (data.success) {
      if (data.attachments && data.attachments.length > 0) {
        // Images were automatically attached
        notify.success(t('imagen.success.attached'));
        setRefreshKey(prev => prev + 1);
        setShowAiTab(false);
      } else if (data.images) {
        // Images generated but not attached - show success
        notify.success(t('imagen.success.generated', { count: data.images.length }));
      }
    }
  };


  // Generate uploader class name based on entity type
  const getUploaderClassName = () => {
    return `${finalEntityType}-uploader`;
  };

  // Generate gallery class name based on entity type
  const getGalleryClassName = () => {
    return `${finalEntityType}-gallery`;
  };

  return (
    <div className={`image-manager image-manager--${finalEntityType} ${className}`}>
      {editable && (showUploader || showAiGenerator) && (
        <div className="image-uploader-section">
          {showAiGenerator && showUploader && (
            <div className="uploader-tabs">
              <button
                type="button"
                className={`tab-button ${!showAiTab ? 'active' : ''}`}
                onClick={() => setShowAiTab(false)}
              >
                <span className="tab-icon">📁</span>
                {t('attachments.upload') || 'Upload'}
              </button>
              <button
                type="button"
                className={`tab-button ${showAiTab ? 'active' : ''}`}
                onClick={() => setShowAiTab(true)}
              >
                <span className="tab-icon">🎨</span>
                {t('imagen.generator.tabTitle') || 'AI Generate'}
              </button>
            </div>
          )}

          {!showAiTab && showUploader && (
            <AttachmentUploader
              entityType={finalEntityType}
              entityId={finalEntityId}
              context={context}
              onUploadSuccess={handleUploadSuccess}
              multiple={true}
              accept="image/*"
              className={getUploaderClassName()}
              maxFileSize={maxFileSize}
            />
          )}

          {showAiTab && showAiGenerator && (
            <ImagenGenerator
              entity={entity}
              onImageGenerated={handleAiImageGenerated}
              compact={true}
              className="ai-generator-tab"
            />
          )}

          {!showUploader && showAiGenerator && (
            <ImagenGenerator
              entity={entity}
              onImageGenerated={handleAiImageGenerated}
              compact={true}
              className="ai-generator-only"
            />
          )}
        </div>
      )}

      <div className="image-gallery-section">
        <AttachmentGallery
          key={refreshKey}
          entityType={finalEntityType}
          entityId={finalEntityId}
          context={context}
          editable={editable}
          onAttachmentDelete={handleAttachmentDelete}
          onAttachmentUpdate={handleAttachmentUpdate}
          className={getGalleryClassName()}
          maxItems={maxImages}
          showTitle={false}
          showInfo={editable}
          thumbnailSize={thumbnailSize}
          displayMode={displayMode}
        />
      </div>
    </div>
  );
};

export default ImageManager;
